import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Clock, 
  ArrowLeft, 
  Play, 
  Pause, 
  RotateCcw, 
  Flag, 
  Maximize2, 
  Minimize2, 
  Sparkles 
} from 'lucide-react';

export const Stopwatch = () => {
  const { withAd, toast } = useAdRedirect();
  const [running, setRunning] = useState(false);
  const [time, setTime] = useState(0); // in milliseconds
  const [laps, setLaps] = useState([]);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const containerRef = useRef(null);

  useEffect(() => {
    let timer;
    if (running) {
      timer = setInterval(() => {
        setTime((prev) => prev + 10);
      }, 10);
    }
    return () => clearInterval(timer);
  }, [running]);

  const toggleRunning = () => {
    setRunning(!running);
  };

  const handleReset = () => {
    setRunning(false);
    setTime(0);
    setLaps([]);
  };

  const handleLap = () => {
    if (!running) return;
    const currentLap = time;
    const prevLapTime = laps.length > 0 ? laps[0].time : 0;
    const splitTime = currentLap - prevLapTime;

    setLaps([{ id: Date.now(), time: currentLap, split: splitTime }, ...laps]);
  };

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullScreen(!isFullScreen);
  };

  // Format mm:ss:ms
  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    const centiseconds = Math.floor((ms % 1000) / 10);

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${centiseconds.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Online Stopwatch - Millisecond Precision" 
        description="Track time with millisecond precision. Start, pause, reset, and log split lap times instantly. Perfect for workouts and studying."
        keywords="online stopwatch, digital stopwatch, lap timer, millisecond stopwatch, time tracker, workout timer"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Stopwatch</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Millisecond Precision Stopwatch & Split Lap Timer
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Record high-fidelity workout sessions, sprints, and study intervals with background tab retention.
        </p>
      </div>

      {/* Main Stopwatch Board */}
      <div 
        ref={containerRef}
        style={{ 
          background: isFullScreen ? '#0f172a' : '#ffffff', 
          border: isFullScreen ? 'none' : '1px solid var(--border-color)', 
          borderRadius: isFullScreen ? '0' : '24px', 
          padding: '40px 24px', 
          boxShadow: isFullScreen ? 'none' : 'var(--shadow-md)',
          textAlign: 'center',
          color: isFullScreen ? '#ffffff' : 'var(--text-primary)',
          transition: 'var(--transition-smooth)',
          minHeight: isFullScreen ? '100vh' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ width: '100%', maxWidth: '700px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          
          {/* Header row in fullscreen */}
          <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: isFullScreen ? '#94a3b8' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={16} style={{ color: 'var(--primary)' }} /> Live Timer Active
            </span>

            <button 
              onClick={toggleFullScreen}
              style={{ background: isFullScreen ? 'rgba(255,255,255,0.1)' : 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '8px 14px', borderRadius: '8px', color: isFullScreen ? '#ffffff' : 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />} {isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
            </button>
          </div>

          {/* Huge Time Display */}
          <div style={{ fontSize: isFullScreen ? '7.5rem' : '5.5rem', fontWeight: 900, fontFamily: 'monospace', letterSpacing: '-2px', color: isFullScreen ? '#ffffff' : 'var(--text-primary)', marginBottom: '36px', lineHeight: 1, textShadow: isFullScreen ? '0 10px 30px rgba(0,0,0,0.5)' : 'none' }}>
            {formatTime(time)}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '40px', width: '100%' }}>
            <button
              onClick={toggleRunning}
              style={{ flex: '1', minWidth: '140px', maxWidth: '200px', padding: '16px 28px', borderRadius: '16px', border: 'none', background: running ? '#f59e0b' : 'var(--primary)', color: '#ffffff', fontSize: '1.1rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'var(--transition-smooth)' }}
            >
              {running ? <Pause size={20} /> : <Play size={20} />} {running ? 'Pause' : 'Start'}
            </button>

            <button
              onClick={handleLap}
              disabled={!running}
              style={{ flex: '1', minWidth: '140px', maxWidth: '200px', padding: '16px 28px', borderRadius: '16px', border: 'none', background: running ? '#10b981' : 'rgba(16, 185, 129, 0.4)', color: '#ffffff', fontSize: '1.1rem', fontWeight: 800, cursor: running ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'var(--transition-smooth)' }}
            >
              <Flag size={20} /> Lap
            </button>

            <button
              onClick={handleReset}
              style={{ flex: '1', minWidth: '140px', maxWidth: '200px', padding: '16px 28px', borderRadius: '16px', border: '1px solid rgba(239, 68, 68, 0.3)', background: 'transparent', color: isFullScreen ? '#ef4444' : '#ef4444', fontSize: '1.1rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'var(--transition-smooth)' }}
            >
              <RotateCcw size={20} /> Reset
            </button>
          </div>

          {/* Split Laps Tracking Table */}
          {laps.length > 0 && (
            <div style={{ width: '100%', background: isFullScreen ? 'rgba(255,255,255,0.05)' : 'var(--bg-secondary)', padding: '24px', borderRadius: '20px', border: isFullScreen ? 'none' : '1px solid var(--border-color)', textAlign: 'left', maxHeight: '300px', overflowY: 'auto' }}>
              <div style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: isFullScreen ? '#ffffff' : 'var(--text-primary)' }}>
                <span>Recorded Split Laps ({laps.length})</span>
                <span style={{ fontSize: '0.8rem', color: isFullScreen ? '#94a3b8' : 'var(--text-secondary)' }}>Format: Total Time / Split Diff</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {laps.map((lap, idx) => (
                  <div key={lap.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: isFullScreen ? 'rgba(0,0,0,0.3)' : '#ffffff', borderRadius: '10px', fontSize: '0.9rem', fontFamily: 'monospace' }}>
                    <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Lap {laps.length - idx}</span>
                    <span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{formatTime(lap.time)}</span>
                    <span style={{ fontWeight: 700, color: '#10b981' }}>+{formatTime(lap.split)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: '28px' }}>
            <AdToast message={toast} />
            <button 
              onClick={withAd('stopwatch-btn', () => alert("Time log exported client-side."))}
              style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '10px 24px', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
            >
              Export Lap Tracking Log
            </button>
          </div>

        </div>
      </div>

      <AdUnit />
      <ToolPageExtras toolId="stopwatch" />
    </div>
  );
};

export default Stopwatch;
